import { useCallback, useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "../../../lib/cn";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../../shadcn/popover";
import { addMinutes, ampm, fromMinutes, parseTyped, toMinutes } from "./nightClock";

type Props = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  size?: "sm" | "md";
  invalid?: boolean;
};

const GROUPS: { name: string; from: number; to: number }[] = [
  { name: "Evening", from: 18 * 60, to: 24 * 60 },
  { name: "Night", from: 0, to: 6 * 60 },
  { name: "Morning", from: 6 * 60, to: 12 * 60 },
  { name: "Afternoon", from: 12 * 60, to: 18 * 60 },
];

const SLOTS = GROUPS.map((g) => ({
  name: g.name,
  times: Array.from({ length: (g.to - g.from) / 15 }, (_, i) => fromMinutes(g.from + i * 15)),
}));

const stepButton = "h-full w-8 text-neutral-600 hover:bg-neutral-100 outline-none focus-visible:bg-neutral-100";

export function TimePicker({ value, onChange, label, size = "md", invalid }: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [highlight, setHighlight] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const small = size === "sm";

  // Cleared on every open.
  useEffect(() => {
    if (!open) return;
    setText("");
    setHighlight(null);
  }, [open]);

  // The popover content mounts after the commit that flips `open`, so the list scrolls to the
  // selected slot (or the first) when its node attaches, not in an effect on `open`.
  const attachList = useCallback((list: HTMLDivElement | null) => {
    listRef.current = list;
    if (!list) return;
    const target = (list.querySelector('[aria-selected="true"]') || list.querySelector('[role="option"]')) as HTMLElement | null;
    if (target) list.scrollTop = target.offsetTop - list.clientHeight / 2 + 16;
  }, []);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const onType = (next: string) => {
    setText(next);
    const v = parseTyped(next);
    if (!v) {
      setHighlight(null);
      return;
    }
    const near = fromMinutes(Math.round(toMinutes(v) / 15) * 15);
    setHighlight(near);
    const node = listRef.current?.querySelector(`[data-time="${near}"]`) as HTMLElement | null;
    if (node && typeof node.scrollIntoView === "function") node.scrollIntoView({ block: "nearest" });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const v = parseTyped(text);
    if (v) pick(v);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          className={cn(
            "inline-flex items-center rounded-[10px] border border-neutral-200 overflow-hidden transition duration-150 ease-soft focus-within:border-brand focus-within:shadow-ring",
            small ? "h-8" : "h-9",
            invalid && "border-red-600 shadow-ring-err"
          )}
        >
          <button type="button" aria-label="15 minutes earlier" className={stepButton} onClick={() => onChange(addMinutes(value, -15))}>
            &minus;
          </button>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-label={`${label}, ${ampm(value)}`}
              aria-invalid={invalid || undefined}
              className={cn(
                "h-full border-x border-neutral-200 font-medium tabular-nums outline-none focus-visible:bg-neutral-100",
                small ? "w-[100px] text-[13px]" : "w-28"
              )}
            >
              {ampm(value)}
            </button>
          </PopoverTrigger>
          <button type="button" aria-label="15 minutes later" className={stepButton} onClick={() => onChange(addMinutes(value, 15))}>
            +
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[200px] p-1"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <input
          ref={inputRef}
          value={text}
          placeholder="Type a time, e.g. 10:40 pm"
          aria-label={`Type ${label}`}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={onKeyDown}
          className="mb-1 h-8 w-full rounded-lg border border-neutral-200 px-2 text-[13px] outline-none focus:border-brand focus:shadow-ring"
        />
        <div ref={attachList} role="listbox" aria-label={label} className="relative max-h-[216px] overflow-y-auto">
          {SLOTS.map((group) => (
            <div key={group.name}>
              <div role="presentation" className="text-[11px] text-neutral-400 px-2 pt-1.5 pb-0.5">
                {group.name}
              </div>
              {group.times.map((v) => {
                const selected = v === value;
                return (
                  <button
                    key={v}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-time={v}
                    data-highlighted={v === highlight || undefined}
                    onClick={() => pick(v)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-neutral-100 data-[highlighted]:bg-neutral-100",
                      selected && "text-brand font-medium"
                    )}
                  >
                    <span>{ampm(v)}</span>
                    {selected && <Check size={12} aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
